import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "react-toastify";
import { z } from "zod";
import HRTitleHeader from "src/components/ui/HRTitleHeader";
import { uploadVideo, submitUploadVideo } from "../../../../redux/api/_hr.api";
import DeleteVideo from "./Delete-videos";

// ---------------- ZOD SCHEMA ----------------
export const videoSchema = z.object({
  name: z.string({ message: "name is required" }),
  post: z.string().min(1, "Post is required"),
  heading: z.string().min(1, "Heading is required"),
  subheading: z.string().min(1, "Subheading is required"),

  thumbnail: z.instanceof(File, { message: "Thumbnail is required" }),

  video_key: z.string().url("Valid video URL is required"),

  thumbnail_key: z.string().optional(),
});

export type VideoFormValues = z.infer<typeof videoSchema>;

const UploadVideo = () => {
  const form = useForm<VideoFormValues>({
    resolver: zodResolver(videoSchema),
  });

  const [loading, setLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0); // Add refresh trigger

  const thumbnailFile = form.watch("thumbnail");
  const thumbnailPreview = thumbnailFile
    ? URL.createObjectURL(thumbnailFile)
    : "";

  const onSubmit = async (values: VideoFormValues) => {
    setLoading(true);

    try {
      // Upload thumbnail → get S3 key
      const thumbnail_key = await uploadVideo(values.thumbnail);

      values.thumbnail_key = thumbnail_key;

      // video_key is already the user-entered URL
      await submitUploadVideo(values);

      toast.success("Video uploaded successfully!");
      form.reset();
      
      // Trigger refresh in DeleteVideo component
      setRefreshTrigger(prev => prev + 1);
    } catch (err) {
      console.error(err);
      toast.error("Upload failed");
    }

    setLoading(false);
  };

  return (
    <>
      <HRTitleHeader
        title="Upload Videos"
        info="Upload videos of campaigns and sessions held at various locations"
      />

      <div className="max-w-xl mx-auto p-6">
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Standard Inputs */}
          <div className="space-y-4">
            {/* Name */}
            <div>
              <label className="font-semibold block mb-1">
                Name <span className="text-red-400">*</span>
              </label>
              <input
                {...form.register("name")}
                placeholder="Enter name"
                className="w-full rounded-xl p-2 border-2 border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>

            {/* Post */}
            <div>
              <label className="font-semibold block mb-1">
                Post <span className="text-red-400">*</span>
              </label>
              <input
                {...form.register("post")}
                placeholder="Enter post"
                className="w-full rounded-xl p-2 border-2 border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>

            {/* Heading */}
            <div>
              <label className="font-semibold block mb-1">
                Heading <span className="text-red-400">*</span>
              </label>
              <input
                {...form.register("heading")}
                placeholder="Enter heading"
                className="w-full rounded-xl p-2 border-2 border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>

            {/* Subheading */}
            <div>
              <label className="font-semibold block mb-1">
                Subheading <span className="text-red-400">*</span>
              </label>
              <input
                {...form.register("subheading")}
                placeholder="Enter subheading"
                className="w-full rounded-xl p-2 border-2 border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              <p className="text-red-400 font-medium">
                Word count:{" "}
                {form
                  .watch("subheading")
                  ?.trim()
                  .split(/\s+/)
                  .filter(Boolean).length || 0}{" "}
                <br />
                *Enter within 10 words*
              </p>
            </div>

            {/* Video URL (now video_key) */}
            <div>
              <label className="font-semibold block mb-1">
                Video URL <span className="text-red-400">*</span>
              </label>

              <input
                {...form.register("video_key")}
                placeholder="Paste video URL"
                className="w-full rounded-xl p-2 border-2 border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />

              {form.formState.errors.video_key && (
                <p className="text-red-500 text-sm">
                  {form.formState.errors.video_key.message}
                </p>
              )}
            </div>
          </div>

          {/* Thumbnail Upload */}
          <div>
            <label className="block mb-1 font-semibold">
              Thumbnail <span className="text-red-400">*</span>
            </label>

            <label className="border-2 border-dashed border-blue-400 rounded-xl p-6 flex flex-col items-center justify-center cursor-pointer hover:bg-blue-50 transition">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-12 h-12 text-blue-500"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 16.5v-9m0 0L8.25 11.25M12 7.5l3.75 3.75M6.75 19.5h10.5A2.25 2.25 0 0019.5 17.25V6.75A2.25 2.25 0 0017.25 4.5H6.75A2.25 2.25 0 004.5 6.75v10.5A2.25 2.25 0 006.75 19.5z"
                />
              </svg>

              <p className="mt-2 text-blue-600 font-medium">
                Click to upload thumbnail
              </p>
              <p className="text-sm text-gray-500">PNG, JPG allowed</p>

              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  if (e.target.files?.length) {
                    form.setValue("thumbnail", e.target.files[0]);
                  }
                }}
                className="hidden"
              />
            </label>

            {thumbnailPreview && (
              <div className="mt-4">
                <img
                  src={thumbnailPreview}
                  className="w-40 h-40 rounded-xl object-cover shadow-lg border"
                />
                <p className="font-semibold text-red-400 mt-1">
                  Click the box again to change file
                </p>
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white p-3 rounded-lg"
          >
            {loading ? "Uploading..." : "Upload"}
          </button>
        </form>
      </div>
      <DeleteVideo refreshTrigger={refreshTrigger} />
    </>
  );
};

export default UploadVideo;